import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { darkDatePickerSlotProps } from "../../../../themes/datePickerStyles";
import {
  Airframe,
  CreateAirframeRequest,
  UpdateAirframeRequest,
} from "../../../../types/airframe";
import { useSnackbar } from "../../../../contexts/snackbar-context";
import { useAirframeTemplateService } from "../../../../api/airframeTemplateService";
import { parseDate } from "../../../../utils/dateUtils";
import { useTenantDisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";
import { useOperatorService } from "../../../../api/operatorService";
import { OwnershipFormFields } from "../../../common/OwnershipFormFields";
import { normalizeOwnershipStatus, normalizeOperatorIdForForm } from "../../../../constants/ownershipStatus";
import { Operator } from "../../../../types/operator";
import { normalizeOperatorsFromApi } from "../../../../utils/operatorUtils";

type Props = {
  formData: Partial<Airframe>;
  selected: Airframe | null;
  isCreate: boolean;
  open: boolean;
  change: (field: keyof Airframe, value: any) => void;
  close: () => void;
  onSave: (
    request: CreateAirframeRequest | UpdateAirframeRequest,
    id?: string
  ) => Promise<void>;
};

type SlotMap = {
  Engine: { location: string }[];
  APU: { location: string }[];
  "Landing Gear": { location: string }[];
};

const COMPONENT_TYPES = ["Engine", "APU", "Landing Gear"] as const;

const emptySlots: SlotMap = {
  Engine: [{ location: "" }],
  APU: [{ location: "" }],
  "Landing Gear": [{ location: "" }],
};

export function AirframeFormDialog({
  formData,
  selected,
  isCreate,
  open,
  change,
  close,
  onSave,
}: Props) {
  const { showError } = useSnackbar();
  const { getAirframeTemplates, getAirframeTemplateById } =
    useAirframeTemplateService();
  const changeRef = useRef(change);
  changeRef.current = change;

  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [slots, setSlots] = useState<SlotMap>(emptySlots);
  const { preferences: displayPreferences } = useTenantDisplayPreferences(open);
  const { getOperators } = useOperatorService();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);

  const airframeOwnershipPreferences = useMemo(
    () => ({
      ...displayPreferences,
      showOwner: false,
    }),
    [displayPreferences]
  );

  // Used to cancel stale async responses if user switches template quickly
  const initializeSlotsFromForm = useCallback(
    (existingSlots?: any[]): SlotMap => {
      const grouped: SlotMap = { Engine: [], APU: [], "Landing Gear": [] };

      if (Array.isArray(existingSlots) && existingSlots.length > 0) {
        existingSlots.forEach((s: any) => {
          const key = s.componentType as keyof SlotMap;
          if (!grouped[key]) return;
          grouped[key].push({ location: s.locationCode || "" });
        });
      }

      return {
        Engine: grouped.Engine.length ? grouped.Engine : [{ location: "" }],
        APU: grouped.APU.length ? grouped.APU : [{ location: "" }],
        "Landing Gear": grouped["Landing Gear"].length
          ? grouped["Landing Gear"]
          : [{ location: "" }],
      };
    },
    []
  );

  // Reset everything when dialog opens/closes
  useEffect(() => {
    if (!open) return;

    if (isCreate) {
      setSelectedTemplateId("");
      setSlots(emptySlots);
    } else {
      setSlots(initializeSlotsFromForm(formData.slots as any[]));
    }
 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isCreate, initializeSlotsFromForm]);

  // Load templates list once when create dialog opens
  useEffect(() => {
    if (!open || !isCreate) return;

    let active = true;
    void getAirframeTemplates()
      .then((items) => {
        if (active) setTemplates(items);
      })
      .catch(() => {
        if (active) setTemplates([]);
      });

    return () => {
      active = false;
    };
  }, [open, isCreate, getAirframeTemplates]);

  useEffect(() => {
    if (!open || !displayPreferences.showOperator) {
      setOperators([]);
      setOperatorsLoading(false);
      return;
    }

    let active = true;
    setOperatorsLoading(true);
    void getOperators(true)
      .then((items) => {
        if (active) setOperators(normalizeOperatorsFromApi(items));
      })
      .catch(() => {
        if (active) setOperators([]);
      })
      .finally(() => {
        if (active) setOperatorsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, getOperators, displayPreferences.showOperator]);

  // Load a selected template — uses a ref for change to avoid re-fetch loops
  useEffect(() => {
    if (!open || !isCreate) return;

    if (!selectedTemplateId) {
      changeRef.current("manufacturer", "");
      changeRef.current("model", "");
      changeRef.current("series", "");
      changeRef.current("commonName", "");
      setSlots(emptySlots);
      setTemplateLoading(false);
      return;
    }

    let active = true;
    setTemplateLoading(true);

    void getAirframeTemplateById(selectedTemplateId)
      .then((tpl: any) => {
        if (!active) return;

        changeRef.current("manufacturer", tpl.manufacturer ?? "");
        changeRef.current("model", tpl.model ?? "");
        changeRef.current("series", tpl.series ?? "");
        changeRef.current("commonName", tpl.commonName ?? "");

        const newSlots: SlotMap = {
          Engine: Array.isArray(tpl.engines)
            ? tpl.engines.map((e: any) => ({
                location: e.label ?? e.position ?? "",
              }))
            : [{ location: "" }],
          APU: Array.isArray(tpl.auxiliaryPowerUnit)
            ? tpl.auxiliaryPowerUnit.map((a: any) => ({
                location: a.label ?? a.position ?? "",
              }))
            : [{ location: "" }],
          "Landing Gear": Array.isArray(tpl.landingGear)
            ? tpl.landingGear.map((l: any) => ({
                location: l.label ?? l.position ?? "",
              }))
            : [{ location: "" }],
        };

        setSlots({
          Engine: newSlots.Engine.length ? newSlots.Engine : [{ location: "" }],
          APU: newSlots.APU.length ? newSlots.APU : [{ location: "" }],
          "Landing Gear": newSlots["Landing Gear"].length
            ? newSlots["Landing Gear"]
            : [{ location: "" }],
        });
      })
      .finally(() => {
        if (active) setTemplateLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedTemplateId, open, isCreate, getAirframeTemplateById]);

  const updateSlotLocation = (
    componentType: keyof SlotMap,
    index: number,
    location: string
  ) => {
    setSlots((prev) => ({
      ...prev,
      [componentType]: prev[componentType].map((s, i) =>
        i === index ? { location } : s
      ),
    }));
  };

  const validateSlots = (): boolean => {
    for (const ct of COMPONENT_TYPES) {
      const hasValid = slots[ct].some((s) => s.location?.trim());
      if (!hasValid) {
        showError(`At least one position is required for ${ct}`);
        return false;
      }
    }
    for (const [componentType, slotArray] of Object.entries(slots)) {
      const locations = slotArray
        .map((s) => s.location?.trim())
        .filter((x): x is string => !!x);
      const duplicates = locations.filter(
        (loc, idx) => locations.indexOf(loc) !== idx
      );
      if (duplicates.length > 0) {
        const uniq: Record<string, true> = {};
        duplicates.forEach((d) => (uniq[d] = true));
        showError(
          `Duplicate assembly locations in ${componentType}: ${Object.keys(uniq).join(", ")}`
        );
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (isSaving) return;

    if (
      !formData.tailNumber ||
      !formData.manufacturer ||
      !formData.model ||
      !formData.serialNumber
    ) {
      showError("Fill all required fields");
      return;
    }

    if (!validateSlots()) return;

    const flatSlots = Object.entries(slots).flatMap(([componentType, arr]) =>
      arr.map((s) => ({ componentType, locationCode: s.location ?? "" }))
    );

    const ownershipPayload = () => {
      const status = normalizeOwnershipStatus(formData.ownershipStatus);
      const operatorId = normalizeOperatorIdForForm(formData.operatorId);
      return {
        ownershipStatus: status !== "" ? status : null,
        operatorId: operatorId || null,
      };
    };

    setIsSaving(true);
    try {
      if (selected?.id) {
        const req: UpdateAirframeRequest = {
          manufacturer: formData.manufacturer,
          model: formData.model,
          tailNumber: formData.tailNumber,
          serialNumber: formData.serialNumber,
          dateManufactured: formData.dateManufactured,
          totalFlightHours: formData.totalFlightHours,
          totalFlightCycles: formData.totalFlightCycles,
          lastHMVDate: formData.lastHMVDate ?? null,
          lastHMVType: formData.lastHMVType ?? null,
          operationalCategory: formData.operationalCategory,
          landingCategory: formData.landingCategory,
          etopsCategory: formData.etopsCategory,
          customFields: formData.customFields ?? null,
          slots: flatSlots,
          updatedBy: "system",
          ...ownershipPayload(),
        };
        await onSave(req, selected.id);
      } else {
        const req: CreateAirframeRequest = {
          manufacturer: formData.manufacturer || "",
          model: formData.model || "",
          tailNumber: formData.tailNumber || "",
          serialNumber: formData.serialNumber || "",
          dateManufactured: formData.dateManufactured,
          totalFlightHours: formData.totalFlightHours,
          totalFlightCycles: formData.totalFlightCycles,
          lastHMVDate: formData.lastHMVDate ?? null,
          lastHMVType: formData.lastHMVType ?? null,
          operationalCategory: formData.operationalCategory,
          landingCategory: formData.landingCategory,
          etopsCategory: formData.etopsCategory,
          customFields: formData.customFields ?? null,
          slots: flatSlots,
          createdBy: "system",
          templateId: selectedTemplateId || undefined,
          ...ownershipPayload(),
        };
        await onSave(req);
      }
      close();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={close}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#111",
            color: "#fff",
            border: "1px solid #333",
          },
        }}
      >
        <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>
          {isCreate ? "Create Airframe" : "Edit Airframe"}
        </DialogTitle>

        <DialogContent>
          {isCreate && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: "#FF6A00", fontWeight: 700 }}>
                Select Template
              </InputLabel>
              <Select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(String(e.target.value))}
                // Never disable the Select — this is what caused the flicker.
                // Show a spinner inside the endAdornment instead.
                endAdornment={
                  templateLoading ? (
                    <CircularProgress
                      size={18}
                      sx={{ color: "#FF6A00", mr: 3 }}
                    />
                  ) : undefined
                }
                sx={{
                  color: "#fff",
                  background: "#222",
                  fontWeight: 700,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#FF6A00",
                  },
                  "& .MuiSvgIcon-root": { color: "#FF6A00" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { background: "#222", color: "#fff", fontWeight: 700 },
                  },
                }}
              >
                <MenuItem value="" sx={{ color: "#fff", background: "#222" }}>
                  None
                </MenuItem>
                {templates.map((tpl) => (
                  <MenuItem
                    key={tpl.id}
                    value={tpl.id}
                    sx={{ color: "#fff", background: "#222" }}
                  >
                    {tpl.manufacturer} {tpl.model} {tpl.series} {tpl.commonName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              mt: 1,
            }}
          >
            <Section title="Basic Information" />

            <TextField
              label="Manufacturer *"
              value={formData.manufacturer || ""}
              disabled
              InputProps={{ readOnly: true }}
              sx={{
                flex: 1,
                "& .MuiInputLabel-root": {
                  color: "rgba(255,255,255,0.7)",
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.7)" },
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#1a1a1a",
                  color: "#fff",
                  "& fieldset": { borderColor: "#333" },
                  "& input": { color: "#fff", WebkitTextFillColor: "#fff" },
                  "&.Mui-disabled": {
                    backgroundColor: "#1a1a1a",
                    "& fieldset": { borderColor: "#333" },
                    color: "#fff",
                    "& input": { color: "#fff", WebkitTextFillColor: "#fff" },
                  },
                },
              }}
            />

            <TextField
              label="Model *"
              value={formData.model || ""}
              disabled
              InputProps={{ readOnly: true }}
              sx={{
                flex: 1,
                "& .MuiInputLabel-root": {
                  color: "rgba(255,255,255,0.7)",
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.7)" },
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#1a1a1a",
                  color: "#fff",
                  "& fieldset": { borderColor: "#333" },
                  "& input": { color: "#fff", WebkitTextFillColor: "#fff" },
                  "&.Mui-disabled": {
                    backgroundColor: "#1a1a1a",
                    "& fieldset": { borderColor: "#333" },
                    color: "#fff",
                    "& input": { color: "#fff", WebkitTextFillColor: "#fff" },
                  },
                },
              }}
            />

            <TextField
              label="Tail Number *"
              value={formData.tailNumber || ""}
              onChange={(e) => change("tailNumber", e.target.value)}
              disabled={!isCreate}
              InputProps={!isCreate ? { readOnly: true } : undefined}
              required
              sx={
                isCreate
                  ? fieldSx
                  : {
                      flex: 1,
                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7)",
                        "&.Mui-disabled": { color: "rgba(255,255,255,0.7)" },
                      },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#1a1a1a",
                        color: "#fff",
                        "& fieldset": { borderColor: "#333" },
                        "& input": { color: "#fff", WebkitTextFillColor: "#fff" },
                        "&.Mui-disabled": {
                          backgroundColor: "#1a1a1a",
                          "& fieldset": { borderColor: "#333" },
                          color: "#fff",
                          "& input": { color: "#fff", WebkitTextFillColor: "#fff" },
                        },
                      },
                    }
              }
            />

            <TextField
              label="Serial Number (S/N) *"
              value={formData.serialNumber || ""}
              onChange={(e) => change("serialNumber", e.target.value)}
              disabled={!isCreate}
              InputProps={!isCreate ? { readOnly: true } : undefined}
              required
              sx={
                isCreate
                  ? fieldSx
                  : {
                      flex: 1,
                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7)",
                        "&.Mui-disabled": { color: "rgba(255,255,255,0.7)" },
                      },
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#1a1a1a",
                        color: "#fff",
                        "& fieldset": { borderColor: "#333" },
                        "& input": { color: "#fff", WebkitTextFillColor: "#fff" },
                        "&.Mui-disabled": {
                          backgroundColor: "#1a1a1a",
                          "& fieldset": { borderColor: "#333" },
                          color: "#fff",
                          "& input": { color: "#fff", WebkitTextFillColor: "#fff" },
                        },
                      },
                    }
              }
            />

            <DatePicker
              label="Date Manufactured"
              value={parseDate(formData.dateManufactured)}
              onChange={(d) =>
                change("dateManufactured", d ? format(d, "yyyy-MM-dd") : "")
              }
              slotProps={darkDatePickerSlotProps}
            />

            <Section title="Flight Details" />

            <TextField
              type="number"
              label="Total Flight Hours"
              value={formData.totalFlightHours ?? ""}
              onChange={(e) =>
                change("totalFlightHours", Number(e.target.value))
              }
              sx={fieldSx}
            />

            <TextField
              type="number"
              label="Total Flight Cycles"
              value={formData.totalFlightCycles ?? ""}
              onChange={(e) =>
                change("totalFlightCycles", Number(e.target.value))
              }
              sx={fieldSx}
            />

            <Section title="Heavy Maintenance Visit (HMV)" />

            <DatePicker
              label="Date Last HMV"
              value={parseDate(formData.lastHMVDate)}
              onChange={(d) =>
                change("lastHMVDate", d ? format(d, "yyyy-MM-dd") : "")
              }
              slotProps={darkDatePickerSlotProps}
            />

            <TextField
              label="Last HMV Type"
              value={formData.lastHMVType || ""}
              onChange={(e) => change("lastHMVType", e.target.value)}
              sx={fieldSx}
            />

            <Section title="Categories" />

            <TextField
              label="Operational Category"
              value={formData.operationalCategory || ""}
              onChange={(e) => change("operationalCategory", e.target.value)}
              sx={fieldSx}
            />

            <TextField
              label="Landing Category"
              value={formData.landingCategory || ""}
              onChange={(e) => change("landingCategory", e.target.value)}
              sx={fieldSx}
            />

            <FormControl fullWidth sx={selectSx}>
              <InputLabel id="etops-category-label">ETOPS Category</InputLabel>
              <Select
                labelId="etops-category-label"
                label="ETOPS Category"
                value={formData.etopsCategory || ""}
                onChange={(e) =>
                  change("etopsCategory", String(e.target.value))
                }
              >
                {[90, 120, 180, 240].map((v) => (
                  <MenuItem key={v} value={String(v)}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Section title="Assemblies" required />

            {COMPONENT_TYPES.map((componentType) => (
              <Accordion
                key={componentType}
                sx={{
                  gridColumn: "1 / -1",
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #333",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                  sx={{
                    backgroundColor: "#222",
                    "& .MuiAccordionSummary-content": { margin: 0 },
                  }}
                >
                  <Typography
                    sx={{ color: "hsl(19 101% 51% / 1)", fontWeight: 600 }}
                  >
                    {componentType}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ backgroundColor: "#1a1a1a", p: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {slots[componentType].map((slot, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          disabled
                          value={componentType}
                          sx={{
                            flex: 1,
                            "& .MuiInputBase-input.Mui-disabled": {
                              color: "rgba(255,255,255,0.6)",
                              WebkitTextFillColor: "rgba(255,255,255,0.6)",
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: "#333" },
                            },
                          }}
                        />

                     <TextField
                          disabled={!isCreate || !!selectedTemplateId}
                          placeholder="Position"
                          value={slot.location}
                          onChange={(e) =>
                            updateSlotLocation(
                              componentType,
                              index,
                              e.target.value
                            )
                          }
                          sx={{
                            flex: 2,
                            "& .MuiInputLabel-root": { color: "#fff" },
                            "& .MuiOutlinedInput-root": {
                              color: "#fff",
                              "& fieldset": { borderColor: "#333" },
                              "&:hover fieldset": {
                                borderColor:
                                  !isCreate || !!selectedTemplateId
                                    ? "#333"
                                    : "hsl(19 101% 51% / 1)",
                              },
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                              color: "#fff",
                              WebkitTextFillColor: "#fff",
                              opacity: 1,
                            },
                          }}
                        />

                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            <TextField
              label="Additional Notes"
              multiline
              rows={3}
              value={formData.customFields || ""}
              onChange={(e) => change("customFields", e.target.value)}
              sx={{ gridColumn: "1 / -1", ...fieldSx }}
            />

            <OwnershipFormFields
              preferences={airframeOwnershipPreferences}
              ownershipStatus={normalizeOwnershipStatus(formData.ownershipStatus)}
              owner=""
              operatorId={normalizeOperatorIdForForm(formData.operatorId)}
              operatorName={formData.operatorName ?? null}
              operators={operators}
              operatorsLoading={operatorsLoading}
              showOperator
              hideOwner
              onOwnershipStatusChange={(v) => change("ownershipStatus", v === "" ? null : v)}
              onOwnerChange={() => undefined}
              onOperatorIdChange={(v) => change("operatorId", v || null)}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} sx={{ color: "#fff" }}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={save}
            disabled={isSaving || (isCreate && !selectedTemplateId)}
            sx={{
              backgroundColor: "hsl(19 101% 51% / 1)",
              "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
              "&:disabled": {
                backgroundColor: "hsl(19 101% 51% / 0.4)",
                color: "#fff",
              },
            }}
          >
            {isSaving ? "Saving..." : isCreate ? "Create" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

function Section({ title, required }: { title: string; required?: boolean }) {
  return (
    <Box sx={{ gridColumn: "1 / -1" }}>
      <Typography
        fontWeight={600}
        sx={{ color: "hsl(19 101% 51% / 1)", mt: 1 }}
      >
        {title}{" "}
        {required && <span style={{ color: "hsl(19 101% 51% / 1)" }}>*</span>}
      </Typography>
    </Box>
  );
}

const fieldSx = {
  "& .MuiInputLabel-root": {
    color: "#fff",
    "& .MuiInputLabel-asterisk": { display: "none" },
  },
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": { borderColor: "#333" },
    "&:hover fieldset": { borderColor: "hsl(19 101% 51% / 1)" },
  },
};

const selectSx = {
  "& .MuiInputLabel-root": { color: "#fff" },
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    "& fieldset": { borderColor: "#333" },
    "&:hover fieldset": { borderColor: "hsl(19 101% 51% / 1)" },
  },
};